/*
 * Aero Extension - Universal A/B Testing Framework (v3.0)
 * -------------------------------------------------------
 * Vanilla JS, no dependencies. Loaded as a Shopify theme asset by
 * snippets/aero-extension-ab-test.liquid (metafield-gated on custom.ab_test_enabled).
 *
 * Supports ALL test types via a single initTest() entry point:
 *   classic_ab | abn_testing | split_url | multivariate | multipage | aa_testing
 *
 * Three ways to author tests (can be combined):
 *   1. Programmatic - call ABTestFramework.initTest({ ... }) yourself.
 *   2. Config       - declare tests in window.AB_TEST_CONFIG (see
 *                     aero-extension-ab-test-config.js); each is auto-run via initTest().
 *   3. Inline       - markup on the page:
 *        <div data-ab-section="blog_cta_v1">
 *          <div data-ab-variant="A"> ...control... </div>
 *          <div data-ab-variant="B" style="display:none"> ...treatment... </div>
 *        </div>
 *      Inline sections auto-init as classic_ab/abn_testing (testId = the section id).
 *
 * Tracking is triple-path: window.dataLayer (GTM) + gtag() + clarity() when present.
 *
 * Event schema (v3):
 *   ab_test_impression : test_id, test_name, test_type, variant, variant_count,
 *                        page_type, timestamp (+ page_position for multipage,
 *                        + combination for multivariate)
 *   ab_test_interaction: test_id, test_name, test_type, variant, interaction_type,
 *                        element_name, page_type, timestamp
 *
 * Public API (window.ABTestFramework / window.AeroABTest):
 *   initTest(testConfig) -> { testId, variant, variants }
 *   trackInteraction(testId, interactionType, elementName, customData)
 *   setGA4UserProperties()
 *   getActiveTests(), getVariant(testId, options)
 *   trackEvent(eventName, data), trackClarityEvent(eventName, data)
 *   applyInline(), applyConfig(), config, context
 */
(function (window, document) {
  'use strict';

  var thisScript = document.currentScript;

  var DEFAULTS = {
    cookiePrefix: 'ab_test_',
    cookieExpiryDays: 365,
    debug: false,
    readyClass: 'ab-ready',
    sectionAttr: 'data-ab-section',
    nameAttr: 'data-ab-section-name',
    variantAttr: 'data-ab-variant',
    elementIdAttr: 'data-ab-element-id',
    trackAttr: 'data-ab-track',
    elementAttr: 'data-ab-element'
  };

  var cfg = {};
  var context = {};
  var activeTests = {};      // testId -> test metadata
  var firedImpressions = {}; // testId -> true

  function parseBool(value) {
    return value === true || value === 'true' || value === '1';
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      if (!src) continue;
      for (var key in src) {
        if (Object.prototype.hasOwnProperty.call(src, key) && src[key] !== undefined) {
          target[key] = src[key];
        }
      }
    }
    return target;
  }

  function log() {
    if (!cfg.debug || !window.console) return;
    var args = ['[AeroABTest]'].concat([].slice.call(arguments));
    window.console.log.apply(window.console, args);
  }

  function isArray(value) {
    return Object.prototype.toString.call(value) === '[object Array]';
  }

  function resolveConfig() {
    var globalCfg = window.AB_TEST_CONFIG;
    var globalOptions = {};
    if (globalCfg && !isArray(globalCfg) && globalCfg.options) {
      globalOptions = globalCfg.options;
    }

    var scriptOptions = {};
    if (thisScript && thisScript.dataset) {
      var d = thisScript.dataset;
      if (d.abDebug !== undefined) scriptOptions.debug = parseBool(d.abDebug);
      if (d.abCookiePrefix !== undefined) scriptOptions.cookiePrefix = d.abCookiePrefix;
      if (d.abCookieDays !== undefined) scriptOptions.cookieExpiryDays = parseInt(d.abCookieDays, 10);
      if (d.abSection !== undefined) scriptOptions.section = d.abSection;
      if (d.abSectionName !== undefined) scriptOptions.sectionName = d.abSectionName;
    }

    cfg = assign({}, DEFAULTS, globalOptions, scriptOptions);
  }

  /* ---------------- Cookies ---------------- */

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  /* ---------------- Variant assignment ----------------
   * weights may be:
   *   - an array aligned to `variants`  e.g. [0.5, 0.5]            (v3 doc shape)
   *   - a map of variant -> weight      e.g. { A: 50, B: 50 }     (legacy/config shape)
   *   - omitted -> equal split
   */

  function weightFor(weights, variant, index) {
    if (!weights) return 1;
    if (isArray(weights)) return (typeof weights[index] === 'number') ? weights[index] : 0;
    return (typeof weights[variant] === 'number') ? weights[variant] : 0;
  }

  function pickVariant(variants, weights) {
    var i;
    var total = 0;
    for (i = 0; i < variants.length; i++) total += weightFor(weights, variants[i], i);
    if (total > 0) {
      var r = Math.random() * total;
      for (i = 0; i < variants.length; i++) {
        r -= weightFor(weights, variants[i], i);
        if (r < 0) return variants[i];
      }
    }
    return variants[Math.floor(Math.random() * variants.length)];
  }

  // Sticky variant per cookie key. cookieKey defaults to the testId.
  function getVariant(testId, options) {
    options = options || {};
    var variants = (options.variants && options.variants.length) ? options.variants : ['A', 'B'];
    var cookieName = cfg.cookiePrefix + (options.cookieKey || testId);
    var variant = getCookie(cookieName);

    if (!variant || variants.indexOf(variant) === -1) {
      variant = pickVariant(variants, options.weights);
      setCookie(cookieName, variant, cfg.cookieExpiryDays);
      log('assigned variant', cookieName, variant);
    } else {
      log('existing variant', cookieName, variant);
    }
    return variant;
  }

  /* ---------------- Page context ---------------- */

  function normalizeType(resourceType) {
    switch (resourceType) {
      case 'article': return 'blog_post';
      case 'product': return 'product';
      case 'collection': return 'collection';
      case 'page': return 'page';
      default: return resourceType || 'unknown';
    }
  }

  function detectTypeFromBody() {
    var cls = document.body ? document.body.className : '';
    if (cls.indexOf('template-article') !== -1) return 'article';
    if (cls.indexOf('template-blog') !== -1) return 'blog';
    if (cls.indexOf('template-product') !== -1) return 'product';
    if (cls.indexOf('template-collection') !== -1) return 'collection';
    if (cls.indexOf('template-page') !== -1) return 'page';
    if (cls.indexOf('template-cart') !== -1) return 'cart';
    if (cls.indexOf('template-index') !== -1) return 'index';
    return '';
  }

  function getPageContext() {
    var page = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) || {};
    var resourceType = page.resourceType || detectTypeFromBody();
    return {
      resourceType: resourceType,
      resourceId: page.resourceId || null,
      pageType: normalizeType(resourceType),
      name: (document.title || '').trim()
    };
  }

  /* ---------------- Tracking (triple-path) ---------------- */

  function pushDataLayer(eventName, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(assign({ event: eventName }, data));
  }

  function trackEvent(eventName, data) {
    data = data || {};
    pushDataLayer(eventName, data);
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, data); } catch (e) { log('gtag error', e); }
    }
    sendToClarity(eventName, data);
    log('track', eventName, data);
  }

  // Clarity is most useful with discrete custom tags (filterable key/value) rather
  // than one JSON blob. We set per-dimension tags AND keep the JSON tag for back-compat.
  function sendToClarity(eventName, data) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('set', eventName, JSON.stringify(data));
      if (data.test_id) window.clarity('set', 'ab_test', String(data.test_id));
      if (data.variant) window.clarity('set', 'ab_variant', String(data.variant));
      if (data.test_type) window.clarity('set', 'ab_test_type', String(data.test_type));
      if (data.page_type) window.clarity('set', 'ab_page_type', String(data.page_type));
      if (data.element_name) window.clarity('set', 'ab_element', String(data.element_name));
      window.clarity('event', eventName);
    } catch (e) { log('clarity error', e); }
  }

  function trackClarityEvent(eventName, data) {
    if (typeof window.clarity === 'function') {
      try { window.clarity('set', 'ab_test_' + eventName, JSON.stringify(data)); } catch (e) { log('clarity error', e); }
    }
  }

  function trackImpression(test, variant, extra) {
    if (firedImpressions[test.testId]) return;
    firedImpressions[test.testId] = true;
    trackEvent('ab_test_impression', assign({
      test_id: test.testId,
      test_name: test.testName || '',
      test_type: test.testType || 'classic_ab',
      variant: variant,
      variant_count: (test.variants && test.variants.length) || 0,
      page_type: test.pageType || context.pageType,
      page_position: test.pagePosition || undefined,
      timestamp: new Date().toISOString()
    }, extra));
  }

  // V3 signature: testId first. customData is merged into the event.
  function trackInteraction(testId, interactionType, elementName, customData) {
    var test = activeTests[testId] || { testId: testId };
    var variant = getCookie(cfg.cookiePrefix + testId) || test.variant || 'unknown';
    trackEvent('ab_test_interaction', assign({
      test_id: testId,
      test_name: test.testName || '',
      test_type: test.testType || 'classic_ab',
      variant: variant,
      interaction_type: interactionType || 'click',
      element_name: elementName || '',
      page_type: test.pageType || context.pageType,
      timestamp: new Date().toISOString()
    }, customData));
  }

  /* ---------------- DOM application ---------------- */

  function closestSection(el) {
    if (el.closest) return el.closest('[' + cfg.sectionAttr + ']');
    var node = el;
    while (node && node !== document) {
      if (node.hasAttribute && node.hasAttribute(cfg.sectionAttr)) return node;
      node = node.parentNode;
    }
    return null;
  }

  // Show only the chosen data-ab-variant within the section that carries sectionId.
  // Returns the list of variant names found (for variant_count) or [] if none.
  function variantNamesIn(sectionEl) {
    var variantEls = sectionEl.querySelectorAll('[' + cfg.variantAttr + ']');
    var available = [];
    for (var i = 0; i < variantEls.length; i++) {
      if (closestSection(variantEls[i]) !== sectionEl) continue;
      var name = variantEls[i].getAttribute(cfg.variantAttr);
      if (available.indexOf(name) === -1) available.push(name);
    }
    return available;
  }

  function showVariantIn(sectionEl, variant) {
    var variantEls = sectionEl.querySelectorAll('[' + cfg.variantAttr + ']');
    for (var i = 0; i < variantEls.length; i++) {
      var el = variantEls[i];
      if (closestSection(el) !== sectionEl) continue;
      el.style.display = (el.getAttribute(cfg.variantAttr) === variant) ? '' : 'none';
    }
    sectionEl.setAttribute('data-ab-active-variant', variant);
  }

  /* ---------------- initTest: the universal entry point ---------------- */

  function initTest(testConfig) {
    if (!testConfig || !testConfig.testId) {
      log('initTest: missing testId', testConfig);
      return null;
    }
    var testType = testConfig.testType || 'classic_ab';
    log('initTest', testConfig.testId, testType);

    if (testType === 'multivariate' && isArray(testConfig.elements)) {
      return initMultivariate(testConfig);
    }

    var sectionId = testConfig.sectionId || testConfig.testId;
    var sectionEl = document.querySelector('[' + cfg.sectionAttr + '="' + sectionId + '"]');

    // Determine the variant universe: explicit list, else the inline DOM variants.
    var variants = (testConfig.variants && testConfig.variants.length)
      ? testConfig.variants
      : (sectionEl ? variantNamesIn(sectionEl) : []);
    if (!variants.length) variants = ['A', 'B'];

    var variant = getVariant(testConfig.testId, {
      variants: variants,
      weights: testConfig.variantWeights || testConfig.weights
    });

    // Apply DOM swap for inline/markup tests.
    if (sectionEl) showVariantIn(sectionEl, variant);

    // Apply config-driven selector mutations if provided (advanced; see config file).
    applyVariantChanges(testConfig, variant);

    // Split URL: redirect to the variant's URL (guard against loops).
    if (testType === 'split_url' && testConfig.urls) {
      redirectToVariantUrl(testConfig.urls, variant);
    }

    var meta = {
      testId: testConfig.testId,
      testName: testConfig.testName || (sectionEl && sectionEl.getAttribute(cfg.nameAttr)) || context.name || '',
      testType: testType,
      pageType: testConfig.pageType || context.pageType,
      pagePosition: testConfig.pagePosition,
      variant: variant,
      variants: variants,
      primaryForUserProperties: !!testConfig.primaryForUserProperties
    };
    activeTests[testConfig.testId] = meta;

    persistSession(meta);
    trackImpression(meta, variant);
    setGA4UserProperties();

    return { testId: testConfig.testId, variant: variant, variants: variants };
  }

  function initMultivariate(testConfig) {
    var elements = testConfig.elements;
    var combinationParts = [];
    var totalVariants = 1;

    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      var elVariants = (element.variants && element.variants.length) ? element.variants : ['A', 'B'];
      totalVariants *= elVariants.length;

      var elVariant = getVariant(testConfig.testId, {
        variants: elVariants,
        weights: element.weights,
        cookieKey: testConfig.testId + '_' + element.elementId
      });
      combinationParts.push(element.elementId + ':' + elVariant);

      var elSectionId = element.sectionId || element.elementId;
      var elSectionEl = document.querySelector('[' + cfg.sectionAttr + '="' + elSectionId + '"]');
      if (elSectionEl) showVariantIn(elSectionEl, elVariant);
    }

    var combination = combinationParts.join('|');
    var meta = {
      testId: testConfig.testId,
      testName: testConfig.testName || context.name || '',
      testType: 'multivariate',
      pageType: testConfig.pageType || context.pageType,
      variant: combination,
      variants: { length: totalVariants },
      primaryForUserProperties: !!testConfig.primaryForUserProperties
    };
    activeTests[testConfig.testId] = meta;

    persistSession(meta);
    trackImpression(meta, combination, { combination: combination });
    setGA4UserProperties();

    return { testId: testConfig.testId, variant: combination, variants: combinationParts };
  }

  function redirectToVariantUrl(urls, variant) {
    var target = urls[variant];
    if (!target) return;
    var current = window.location.pathname + window.location.search;
    if (target !== current) {
      log('split_url redirect', variant, target);
      window.location.href = target;
    }
  }

  function persistSession(meta) {
    try {
      sessionStorage.setItem(cfg.cookiePrefix + 'session_' + meta.testId, JSON.stringify({
        testId: meta.testId,
        testName: meta.testName,
        testType: meta.testType,
        variant: meta.variant,
        timestamp: new Date().toISOString()
      }));
    } catch (e) { /* sessionStorage may be unavailable; non-fatal */ }
  }

  /* ---------------- Config-driven selector mutations (advanced) ---------------- */

  function applyVariantChanges(testConfig, variant) {
    // Optional: testConfig.changes = { A: [ops], B: [ops] } applies DOM ops by selector,
    // letting you A/B test existing markup without data-ab-variant divs.
    var changesByVariant = testConfig.changes;
    if (!changesByVariant) return;
    var ops = changesByVariant[variant];
    if (!isArray(ops)) return;
    for (var i = 0; i < ops.length; i++) applyChange(ops[i]);
  }

  function applyChange(change) {
    if (!change || !change.selector) return;
    var els = document.querySelectorAll(change.selector);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (change.text !== undefined) el.textContent = change.text;
      if (change.html !== undefined) el.innerHTML = change.html;
      if (change.attr) {
        for (var a in change.attr) {
          if (Object.prototype.hasOwnProperty.call(change.attr, a)) el.setAttribute(a, change.attr[a]);
        }
      }
      if (change.style) {
        for (var s in change.style) {
          if (Object.prototype.hasOwnProperty.call(change.style, s)) el.style[s] = change.style[s];
        }
      }
      if (change.addClass) asList(change.addClass).forEach(function (c) { if (c) el.classList.add(c); });
      if (change.removeClass) asList(change.removeClass).forEach(function (c) { if (c) el.classList.remove(c); });
      if (change.show) el.style.display = '';
      if (change.hide) el.style.display = 'none';
    }
  }

  function asList(value) {
    if (isArray(value)) return value;
    return String(value).split(/\s+/);
  }

  /* ---------------- Auto-run: inline markup ---------------- */

  function applyInline() {
    var sections = document.querySelectorAll('[' + cfg.sectionAttr + ']');
    for (var i = 0; i < sections.length; i++) {
      var sectionEl = sections[i];
      var sectionId = sectionEl.getAttribute(cfg.sectionAttr);
      if (!sectionId || activeTests[sectionId]) continue;
      var variants = variantNamesIn(sectionEl);
      if (!variants.length) continue;
      initTest({
        testId: sectionId,
        testName: sectionEl.getAttribute(cfg.nameAttr) || context.name || '',
        testType: variants.length > 2 ? 'abn_testing' : 'classic_ab',
        sectionId: sectionId,
        variants: variants
      });
    }
  }

  /* ---------------- Auto-run: config ---------------- */

  function resolveTests() {
    var c = window.AB_TEST_CONFIG;
    if (!c) return [];
    if (isArray(c)) return c;
    if (isArray(c.tests)) return c.tests;
    return [];
  }

  function matchesPage(test) {
    var m = test.match;
    if (!m) return true;
    if (m.pageType && m.pageType !== context.pageType) return false;
    if (m.resourceType && m.resourceType !== context.resourceType) return false;
    if (m.resourceId != null && String(m.resourceId) !== String(context.resourceId)) return false;
    if (m.urlContains && window.location.href.indexOf(m.urlContains) === -1) return false;
    if (m.selector && !document.querySelector(m.selector)) return false;
    return true;
  }

  function applyConfig() {
    var tests = resolveTests();
    for (var i = 0; i < tests.length; i++) {
      var test = tests[i];
      var testId = test.testId || test.id;
      if (!testId) { log('config test missing testId', test); continue; }
      if (activeTests[testId]) continue;
      if (!matchesPage(test)) continue;
      // Normalize legacy `id`/`name` keys to v3 names.
      initTest(assign({}, test, {
        testId: testId,
        testName: test.testName || test.name,
        testType: test.testType || (test.elements ? 'multivariate' : 'classic_ab')
      }));
    }
  }

  /* ---------------- Declarative interaction tracking ---------------- */

  function wireDelegatedTracking() {
    /* Announce ownership of [data-ab-track] clicks. The rail ships its own
       standalone telemetry (aero-extension-product-rail.js) for when this
       framework is gated OFF; it checks this flag AT CLICK TIME and stands down
       while the framework is live, so the same click never fires twice. */
    window.AeroABTestWired = true;
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[' + cfg.trackAttr + ']') : null;
      if (!el) return;

      var spec = el.getAttribute(cfg.trackAttr) || 'click';
      var parts = spec.split(':');
      var interactionType = parts[0] || 'click';
      var elementName = parts[1] || el.getAttribute(cfg.elementAttr) || '';

      var sectionEl = closestSection(el);
      var testId = sectionEl ? sectionEl.getAttribute(cfg.sectionAttr) : (cfg.section || context.pageType || 'page');

      trackInteraction(testId, interactionType, elementName);
    }, false);
  }

  /* ---------------- GA4 user properties ---------------- */

  /**
   * Publish the visitor's variant assignment as GA4 USER PROPERTIES.
   *
   * Why this matters: the framework tags `variant` only on its own ab_test_* events, so GA4
   * cannot attribute a `purchase` to a variant from event parameters alone. A user property is
   * what carries the assignment across every other event in the session — the only way to
   * answer "did variant B actually sell more".
   *
   * TWO SINKS, because a gtag-only implementation is a silent no-op on a GTM-only store.
   * Verified 2026-08-18 on trudell-animal-health: `typeof window.gtag === 'undefined'` while
   * GTM (GTM-NTLJDSG) and dataLayer are both present — so the original early return
   * (`if (typeof window.gtag !== 'function') return;`) meant user properties were NEVER set
   * and revenue could not be split by variant.
   *
   * The dataLayer push is therefore the primary path here, not a fallback. Bind it in GTM with
   * a Data Layer Variable on `ab_user_properties` and map it into the GA4 tag's User Properties
   * field. See the tracking contract in the Website Analysis Dashboard repo at
   * src/features/ab-test/docs/GA4_AND_CLARITY_WIRING.md — it has never lived in this repo,
   * despite the path this comment used to give (documentation/GA4_AND_CLARITY_WIRING.md).
   */
  function setGA4UserProperties() {
    var props = {};
    var any = false;
    for (var testId in activeTests) {
      if (!Object.prototype.hasOwnProperty.call(activeTests, testId)) continue;
      var test = activeTests[testId];
      props['ab_test_' + testId] = test.variant;
      props['ab_test_type_' + testId] = test.testType;
      any = true;
    }
    if (!any) return;

    /* gtag path — for a store that loads gtag.js directly. */
    if (typeof window.gtag === 'function') {
      try { window.gtag('set', 'user_properties', props); } catch (e) { log('gtag set error', e); }
    }

    /* dataLayer path — the one that works on a GTM-only store. Emitted as a NAMED event so a
       GTM trigger can fire a tag that applies the properties, rather than hoping something
       happens to read a bare push.
    
       ALSO emits SHORT, FLAT keys (`ab_test` / `ab_variant`), and they are the ones to bind in
       GTM. Reason: a GA4 user-property NAME is capped at 24 characters, and the generated
       `ab_test_<testId>` names blow past it as soon as a testId is descriptive —
       `ab_test_product_rail_sitewide` is 29 chars, so GA4 silently DROPS it. The flat keys are
       fixed-length and always valid. (Values are capped at 36 chars; variant names are short.)
    
       A single `ab_variant` cannot represent two assignments, so with concurrent tests we pick
       ONE to report flat: the test whose config sets `primaryForUserProperties: true`, else the
       first active test in registration order. The rest remain in the `ab_user_properties`
       object (subject to the 24-char ceiling on their testIds).
    
       This deliberately does NOT go silent when a second test activates. Emitting nothing was
       the earlier behaviour and it is the more dangerous failure: the GTM tag binds the flat
       keys, so a second concurrent test would hand it `undefined` and revenue attribution
       would break with no error anywhere. Reporting the primary test and logging the rest
       degrades instead of disappearing. */
    var payload = { ab_user_properties: props };
    var ids = Object.keys(activeTests);
    var flatId = null;
    if (ids.length === 1) {
      flatId = ids[0];
    } else if (ids.length > 1) {
      for (var i = 0; i < ids.length; i++) {
        if (activeTests[ids[i]] && activeTests[ids[i]].primaryForUserProperties) {
          flatId = ids[i];
          break;
        }
      }
      if (!flatId) flatId = ids[0];
      log(
        'multiple active tests (' + ids.join(', ') + '); reporting flat ab_test/ab_variant for "' +
        flatId + '". Others are in ab_user_properties only — set primaryForUserProperties on the ' +
        'test you want attributed, and keep its testId within GA4\'s 24-char user-property limit.'
      );
    }
    if (flatId) {
      payload.ab_test = flatId;
      payload.ab_variant = activeTests[flatId].variant;
    }
    try {
      pushDataLayer('ab_user_properties', payload);
    } catch (e) {
      log('dataLayer user properties error', e);
    }
  }

  /* ---------------- Init ---------------- */

  function init() {
    try {
      resolveConfig();
      context = getPageContext();
      log('init', context, cfg);

      applyInline();
      applyConfig();
      wireDelegatedTracking();
    } catch (e) {
      log('init error', e);
    } finally {
      // Always reveal content, even if rendering threw, so the anti-flicker
      // style can never leave a [data-ab-section] permanently hidden.
      if (document.documentElement) {
        document.documentElement.classList.add(cfg.readyClass);
      }
    }
  }

  var api = {
    initTest: initTest,
    trackInteraction: trackInteraction,
    setGA4UserProperties: setGA4UserProperties,
    getActiveTests: function () { return activeTests; },
    getVariant: getVariant,
    trackEvent: trackEvent,
    trackClarityEvent: trackClarityEvent,
    applyInline: applyInline,
    applyConfig: applyConfig,
    get config() { return cfg; },
    get context() { return context; }
  };

  window.ABTestFramework = api;
  window.AeroABTest = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
